/**
 * ICS (iCalendar) file generator for recurring workout schedules
 * 
 * Generates .ics files with RRULE (recurrence rules) for weekly recurring events.
 * Each time slot becomes a weekly recurring event with a 2-hour advance reminder.
 */

import type { TimeSlot } from '../components/scheduling/AvailabilityPicker';

// RFC 5545 day names (Sunday = SU, Monday = MO, etc.)
const DAY_NAMES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const DAY_FULL_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Format a date to iCalendar DATETIME format (YYYYMMDDTHHMMSS)
 */
function formatICalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Get the next occurrence of a specific day of week and hour
 */
function getNextOccurrence(dayOfWeek: number, hour: number): Date {
  const now = new Date();
  const result = new Date(now);
  
  // Set to the desired hour
  result.setHours(hour, 0, 0, 0);
  
  // Calculate days until next occurrence
  const currentDay = now.getDay();
  let daysUntil = dayOfWeek - currentDay;
  
  // If the day has passed this week, or it's today but the hour has passed, go to next week
  if (daysUntil < 0 || (daysUntil === 0 && now.getHours() >= hour)) {
    daysUntil += 7;
  }
  
  result.setDate(now.getDate() + daysUntil);
  return result;
}

/**
 * Generate a single VEVENT for a recurring workout time slot
 */
function generateVEvent(slot: TimeSlot, uid: string): string {
  const startDate = getNextOccurrence(slot.dayOfWeek, slot.startHour);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
  
  const dayName = DAY_FULL_NAMES[slot.dayOfWeek];
  const hour12 = slot.startHour > 12 ? slot.startHour - 12 : slot.startHour;
  const ampm = slot.startHour >= 12 ? 'PM' : 'AM';
  const formattedHour = slot.startHour === 12 ? '12' : String(hour12);
  
  const summary = `Workout - ${dayName} ${formattedHour}${ampm}`;
  const description = `Your scheduled workout session. Stay consistent and crush your goals!`;
  
  // Weekly recurrence rule for this specific day
  const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${DAY_NAMES[slot.dayOfWeek]}`;
  
  const lines = [
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatICalDateTime(new Date())}`,
    `DTSTART:${formatICalDateTime(startDate)}`,
    `DTEND:${formatICalDateTime(endDate)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    rrule,
    // 2-hour advance reminder
    'BEGIN:VALARM',
    'TRIGGER:-PT2H',
    'ACTION:DISPLAY',
    `DESCRIPTION:Workout reminder: ${summary}`,
    'END:VALARM',
    'END:VEVENT',
  ];
  
  return lines.join('\r\n');
}

/**
 * Generate a complete ICS file with all recurring workout events
 */
export function generateRecurringWorkoutICS(slots: TimeSlot[]): string {
  const now = new Date();
  const timestamp = now.getTime();
  
  const header = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Start Grounded//Workout Schedule//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:Start Grounded Workouts',
    'X-WR-TIMEZONE:America/Phoenix',
  ].join('\r\n');
  
  const events = slots.map((slot, index) => {
    // Generate unique UID for each event
    const uid = `workout-${timestamp}-${index}@startgrounded.app`;
    return generateVEvent(slot, uid);
  }).join('\r\n');
  
  const footer = 'END:VCALENDAR';
  
  return `${header}\r\n${events}\r\n${footer}`;
}

/**
 * Download an ICS file to the user's device
 */
export function downloadICS(content: string, filename: string = 'start-grounded-workouts.ics') {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
